"use client";

import { useState, useEffect } from "react";
import styles from "./SongPage.module.css";
import { useAudioPlayer } from "@/context/AudioPlayerContext";
import ShareMenuButton from "@/components/ShareMenuButton";
import PlaylistSlider from "@/components/PlaylistSlider"; // 👈 импортируем слайдер
import SectionWithPlayAll from "@/components/SectionWithPlayAll"; // 👈 импортируем секцию

interface Song {
  id: number;
  title: string;
  imageUrl?: string;
  composer?: string;
  duration?: string;
  slug?: string;
  isFavorited?: boolean;
}
interface CoverSources {
  src: string;
  srcSet?: string;
}

interface Props {
  song: Song;
  coverSources: CoverSources;
}

export default function SongPageClient({ song, coverSources }: Props) {
  const { currentTrack, isPlaying, playTrack, pause, resume } =
    useAudioPlayer();
  const [recommendations, setRecommendations] = useState<unknown[]>([]);

  useEffect(() => {
    const loadRecommendations = async () => {
      try {
        const res = await fetch(`/api/song-recommendations?songId=${song.id}`);
        const data = await res.json();

        const recs = (data.recommendations || []).map((r) => {
          if (r._type === "playlist") return { ...r, type: "playlist" };
          if (r._type === "work") return { ...r, type: "work" };
          if (r._type === "song") return { ...r, type: "song" };
          return r;
        });

        setRecommendations(recs);
      } catch (err) {
        console.error("Failed to load recommendations:", err);
      }
    };

    loadRecommendations();
  }, [song.id]);

  const isThisSongPlaying = currentTrack?.id === song.id;

  const handlePlayClick = () => {
    if (isThisSongPlaying) {
      resume();
    } else {
      playTrack({ ...song, slug: song.slug, _type: "song" });
    }
  };

  const handlePauseClick = () => pause();

  return (
    <div className={styles.container}>
      <div className={styles.coverWrapper}>
        <img
          src={coverSources.src}
          srcSet={coverSources.srcSet}
          sizes="(max-width: 1200px) calc(100vw - 2rem), calc(100vw - 6rem)"
          alt={song.title}
          className={styles.cover}
        />
      </div>
      <div className={styles.info}>
        <h2 className={styles.title}>{song.title}</h2>
        <p className={styles.composer}>{song.composer}</p>

        <div className={styles.actionRow}>
          {isThisSongPlaying && isPlaying ? (
            <button
              onClick={handlePauseClick}
              className={styles.playButton}
              aria-label="Pause"
            >
              <img
                src="/icons/pause100-new.png"
                alt="Pause"
                className={styles.icon}
              />
            </button>
          ) : (
            <button
              onClick={handlePlayClick}
              className={styles.playButton}
              aria-label="Play"
            >
              <img
                src="/icons/play-new.png"
                alt="Play"
                className={styles.icon}
              />
            </button>
          )}
          <ShareMenuButton item={song} type="song" />
        </div>
      </div>

      <SectionWithPlayAll
        title="Recommended for You"
        titleClassName={styles.customSectionTitle}
        songs={recommendations} // 👈 добавляем сюда рекомендации
      >
        <PlaylistSlider items={recommendations} />
      </SectionWithPlayAll>
    </div>
  );
}
